import type { ChatResponse } from '../services/chatService';
import type { UIMessage, ProductRecommendation, ChatOption, QuizState, QuizAnswer } from '../types/chat';

export const transformBackendResponseToUIMessage = (
  response: ChatResponse,
  timestamp: string
): UIMessage => {
  return {
    type: 'assistant',
    content: response.answer,
    author: 'Solution assistant',
    timestamp,
    options: response.options,
    export_as: 'assistant',
  };
};

export const transformOptionsToUIOptions = (options: string[]): ChatOption[] => {
  return options.map((option, index) => ({
    id: `option-${index}`,
    label: option,
    value: option,
    isSelected: false,
    disabled: false,
  }));
};

export const transformProductToRecommendation = (
  product: Record<string, unknown> | null | undefined,
  isTopRecommendation = false
): ProductRecommendation | null => {
  if (!product) return null;

  const {
    name = 'Unknown Product',
    description = 'Product description not available',
    image = '/av-logo.png',
    url,
    tags = [],
    features = [],
    key_features = [],
    model
  } = product;

  // Use key_features if available, otherwise fall back to features
  const productFeatures = Array.isArray(key_features) && key_features.length > 0 
    ? key_features 
    : features;

  // Extract model name using best practices
  const extractedModel = model 
    ? String(model)
    : extractModelFromProductName(String(name));

  return {
    productName: String(name),
    description: String(description),
    imageUrl: String(image),
    productUrl: url ? String(url) : undefined,
    tags: Array.isArray(tags) ? tags.map(String) : [],
    matchPercentage: 95, // Default match percentage - could be enhanced later
    featureDescription: Array.isArray(productFeatures) 
      ? productFeatures.map(String).join('. ') 
      : 'Advanced features for your space.',
    features: Array.isArray(productFeatures) ? productFeatures.map(String) : [],
    isTopRecommendation,
    model: extractedModel,
  };
};

// Helper function to extract model name from product name
const extractModelFromProductName = (productName: string): string => {
  // Look for patterns like UC-M70-UA, CCS-UC-1, etc.
  const modelMatch = productName.match(/([A-Z]{2,}-[A-Z0-9-]+)/i);
  if (modelMatch) {
    return modelMatch[1];
  }
  
  // Fallback: return the product name itself
  return productName;
};

// Time formatting utility
const formatTimestamp = (): string => {
  return new Date().toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

export const createUserMessage = (content: string): UIMessage => {
  return {
    type: 'user-response',
    content,
    timestamp: formatTimestamp(),
    export_as: 'user',
  };
};

export const createLoadingMessage = (): UIMessage => {
  return {
    type: 'loading',
    content: '',
    author: 'Solution assistant',
    timestamp: formatTimestamp(),
  };
};

export const getCurrentTimeString = (): string => {
  return formatTimestamp();
};

// Quiz configuration - all 5 questions
export const QUIZ_QUESTIONS = [
  {
    number: 1,
    question: "What type of room are you setting up?",
    content: "To get started, what size is the meeting room where you'll be using the AV system?",
    options: [
      "Small Room / Huddle Space",
      "Medium Room",
      "Large Room",
      "Personal Office / Desk",
      "I have a custom space (integrator kit)"
    ]
  },
  {
    number: 2,
    question: "What will be the primary activity in this room?",
    content: "What will be the primary activity in this room?",
    options: [
      "Video Collaboration",
      "Audio Conferencing Only"
    ]
  },
  {
    number: 3,
    question: "Which platform will you be using for your video conferences?",
    content: "Which platform will you be using for your video conferences?",
    options: [
      "Microsoft Teams",
      "Zoom",
      "Bring Your Own Device (BYOD)"
    ]
  },
  {
    number: 4,
    question: "How would you like the equipment to be set up in the room?",
    content: "How would you like the equipment to be set up in the room?",
    options: [
      "Tabletop Console",
      "Soundbar",
      "All-In-One Bar",
      "Compact Tabletop",
      "Integration Kit"
    ]
  },
  {
    number: 5,
    question: "How far will people be sitting from the microphone?",
    content: "How far will people be sitting from the microphone?",
    options: [
      "Up to 10 ft (3m)",
      "Up to 15 ft (4.5m)",
      "Up to 20 ft (6m)",
      "Up to 30 ft (9m)",
      "System Dependent / Not Sure"
    ]
  }
];

// Initialize quiz state
export const createInitialQuizState = (): QuizState => {
  return {
    isActive: true,
    currentQuestionNumber: 1,
    totalRequiredQuestions: 3,
    answers: [],
    isComplete: false,
  };
};

// Create a quiz question message
export const createQuizQuestionMessage = (questionNumber: number, totalQuestions?: number): UIMessage => {
  const question = QUIZ_QUESTIONS[questionNumber - 1];
  return {
    type: 'question',
    content: question.content,
    questionNumber: question.number,
    totalQuestions: totalQuestions || QUIZ_QUESTIONS.length,
    timestamp: formatTimestamp(),
    export_as: 'assistant',
  };
};

// Add quiz answer and check if quiz is complete
export const addQuizAnswer = (
  quizState: QuizState,
  questionNumber: number,
  answer: string
): QuizState => {
  const question = QUIZ_QUESTIONS[questionNumber - 1];
  const newAnswer: QuizAnswer = {
    questionNumber,
    question: question.question,
    answer,
  };

  const updatedAnswers = [...quizState.answers, newAnswer];
  const isComplete = updatedAnswers.length >= QUIZ_QUESTIONS.length;
  
  return {
    ...quizState,
    answers: updatedAnswers,
    currentQuestionNumber: isComplete ? questionNumber : questionNumber + 1,
    isComplete,
    isActive: !isComplete,
  };
};

// Get options for current quiz question
export const getCurrentQuizOptions = (questionNumber: number): ChatOption[] => {
  if (questionNumber > QUIZ_QUESTIONS.length) return [];
  
  const question = QUIZ_QUESTIONS[questionNumber - 1];
  return transformOptionsToUIOptions(question.options);
};

// Format quiz answers for sending to backend
export const formatQuizAnswersForBackend = (answers: QuizAnswer[]): string => {
  return answers
    .map(answer => `${answer.question}: ${answer.answer}`)
    .join('\n');
};

// Create a summary message for the first 3 answers
export const createAnswerSummaryMessage = (answers: QuizAnswer[]): UIMessage => {
  const first3Answers = answers.slice(0, 3);
  const bulletPoints = first3Answers
    .map(answer => `• ${answer.answer}`)
    .join('\n');
  
  const content = `Searching for products based on your answers:\n${bulletPoints}`;
  
  return {
    type: 'assistant',
    content,
    author: 'Solution assistant',
    timestamp: formatTimestamp(),
  };
};


